import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import {
  Campaign,
  fetchCampaign,
  scheduleCampaign,
  pauseCampaign,
  resumeCampaign,
  cancelCampaign,
  runCampaign,
  sendCampaignDirect,
  sendCampaignDirectV2,
  hasTranslations,
  deleteCampaign,
  getStatusColor,
  getTypeColor,
  getMarketingApiBaseUrl,
} from '../services/campaignApi';
import { fetchWithAuth } from '../utils/auth';

interface Run {
  _id: string;
  campaignId: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  startedAt?: string;
  completedAt?: string;
  stats: {
    targeted: number;
    sent: number;
    delivered: number;
    opened: number;
    clicked: number;
    failed: number;
    skipped: number;
    skipReasons?: Record<string, number>;
  };
  errorMessage?: string;
  createdAt: string;
}

const CampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [runsLoading, setRunsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadCampaign = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCampaign(id);
      setCampaign(data);
    } catch (err: any) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadRuns = useCallback(async () => {
    if (!id) return;
    setRunsLoading(true);
    try {
      const response = await fetchWithAuth(`${getMarketingApiBaseUrl()}/campaigns/${id}/runs`);
      if (response.ok) {
        const data = await response.json();
        setRuns(data.runs || []);
      }
    } catch (err) {
      // Silently fail for runs - they may not be implemented yet
    } finally {
      setRunsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCampaign();
    loadRuns();
  }, [loadCampaign, loadRuns]);

  const handleAction = async (action: string) => {
    if (!id) return;

    try {
      switch (action) {
        case 'schedule':
          await scheduleCampaign(id);
          toast.success('Campaign scheduled');
          break;
        case 'pause':
          await pauseCampaign(id);
          toast.success('Campaign paused');
          break;
        case 'resume':
          await resumeCampaign(id);
          toast.success('Campaign resumed');
          break;
        case 'cancel':
          if (window.confirm('Are you sure you want to cancel this campaign?')) {
            await cancelCampaign(id);
            toast.success('Campaign cancelled');
          }
          break;
        case 'run':
          const result = await runCampaign(id);
          toast.success(`Campaign triggered! Run ID: ${result.runId}`);
          loadRuns();
          break;
        case 'send-direct':
          if (window.confirm('Send notifications directly to all targeted users now?')) {
            // With per-language text, send through the V2 route so each user gets their language.
            // Without it, the original route, exactly as before.
            const directResult = hasTranslations(campaign?.translations)
              ? await sendCampaignDirectV2(id)
              : await sendCampaignDirect(id);
            toast.success(`Sent ${directResult.stats.sent}/${directResult.stats.targeted} notifications`);
            if (directResult.stats.errors?.length) {
              directResult.stats.errors.forEach(e => toast.error(e));
            }
            loadRuns();
            loadCampaign();
          }
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this campaign?')) {
            await deleteCampaign(id);
            toast.success('Campaign deleted');
            navigate('/campaigns');
            return;
          }
          break;
      }
      loadCampaign();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const getRunStatusColor = (status: Run['status']): string => {
    switch (status) {
      case 'PENDING': return 'bg-gray-500';
      case 'IN_PROGRESS': return 'bg-blue-500';
      case 'COMPLETED': return 'bg-green-500';
      case 'FAILED': return 'bg-red-500';
      case 'CANCELLED': return 'bg-yellow-500';
      default: return 'bg-gray-400';
    }
  };

  const brandGradient = 'from-[#FF9C15] to-[#F42026]';

  if (loading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        <Navbar />
        <div className='flex justify-center items-center py-40'>
          <div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF9C15]'></div>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
        <Navbar />
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
          <div className='bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center'>
            <h2 className='text-xl font-semibold text-red-400 mb-2'>Campaign Not Found</h2>
            <p className='text-gray-400 mb-4'>{error || 'The campaign you are looking for does not exist.'}</p>
            <button
              onClick={() => navigate('/campaigns')}
              className={`px-6 py-2 bg-gradient-to-r ${brandGradient} text-white font-semibold rounded-lg`}
            >
              Back to Campaigns
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Header */}
        <div className='flex flex-col sm:flex-row justify-between items-start gap-4 mb-8'>
          <div>
            <button
              onClick={() => navigate('/campaigns')}
              className='flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors'
            >
              <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 19l-7-7m0 0l7-7m-7 7h18' />
              </svg>
              Back to Campaigns
            </button>
            <h1 className='text-3xl font-bold text-white'>{campaign.name}</h1>
            {campaign.description && <p className='text-gray-400 mt-1'>{campaign.description}</p>}
            <div className='flex gap-3 mt-3'>
              <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full text-white ${getTypeColor(campaign.type)}`}>
                {campaign.type.replace('_', ' ')}
              </span>
              <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full text-white ${getStatusColor(campaign.status)}`}>
                {campaign.status}
              </span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className='flex flex-wrap gap-2'>
            {/* Duplicate button - always visible */}
            <button
              onClick={() => navigate('/campaigns/new', { state: { duplicateFrom: campaign } })}
              className='flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg transition-colors'
            >
              <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' />
              </svg>
              Duplicate
            </button>
            {campaign.status === 'DRAFT' && (
              <>
                <button
                  onClick={() => navigate(`/campaigns/${id}/edit`)}
                  className='flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
                  </svg>
                  Edit
                </button>
                <button
                  onClick={() => handleAction('schedule')}
                  className='flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' />
                  </svg>
                  Schedule
                </button>
                <button
                  onClick={() => handleAction('delete')}
                  className='flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                  </svg>
                  Delete
                </button>
              </>
            )}
            {campaign.status === 'SCHEDULED' && (
              <>
                <button
                  onClick={() => handleAction('send-direct')}
                  className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${brandGradient} text-white rounded-lg transition-colors`}
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 19l9 2-9-18-9 18 9-2zm0 0v-8' />
                  </svg>
                  Send Now
                </button>
                <button
                  onClick={() => handleAction('run')}
                  className='flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z' />
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                  </svg>
                  Queue Run
                </button>
                <button
                  onClick={() => handleAction('pause')}
                  className='flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg transition-colors'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z' />
                  </svg>
                  Pause
                </button>
                <button
                  onClick={() => handleAction('cancel')}
                  className='flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                  </svg>
                  Cancel
                </button>
              </>
            )}
            {campaign.status === 'RUNNING' && (
              <button
                onClick={() => handleAction('pause')}
                className='flex items-center gap-2 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg transition-colors'
              >
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z' />
                </svg>
                Pause
              </button>
            )}
            {campaign.status === 'PAUSED' && (
              <>
                <button
                  onClick={() => navigate(`/campaigns/${id}/edit`)}
                  className='flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' />
                  </svg>
                  Edit
                </button>
                <button
                  onClick={() => handleAction('resume')}
                  className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${brandGradient} text-white rounded-lg transition-colors`}
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z' />
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                  </svg>
                  Resume
                </button>
                <button
                  onClick={() => handleAction('cancel')}
                  className='flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors'
                >
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                  </svg>
                  Cancel
                </button>
              </>
            )}
            {campaign.status === 'CANCELLED' && (
              <button
                onClick={() => handleAction('delete')}
                className='flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors'
              >
                <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' />
                </svg>
                Delete
              </button>
            )}
          </div>
        </div>

        {/* Campaign Details Grid */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8'>
          {/* Stats Card */}
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6'>
            <h3 className='text-lg font-semibold text-white mb-4'>Campaign Stats</h3>
            <div className='grid grid-cols-2 gap-4'>
              <div className='bg-white/5 rounded-lg p-4'>
                <div className='text-2xl font-bold text-white'>{campaign.stats?.totalSent ?? 0}</div>
                <div className='text-sm text-gray-400'>Sent</div>
              </div>
              <div className='bg-white/5 rounded-lg p-4'>
                <div className='text-2xl font-bold text-white'>{campaign.stats?.totalDelivered ?? 0}</div>
                <div className='text-sm text-gray-400'>Delivered</div>
              </div>
              <div className='bg-white/5 rounded-lg p-4'>
                <div className='text-2xl font-bold text-white'>{campaign.stats?.totalOpened ?? 0}</div>
                <div className='text-sm text-gray-400'>Opened</div>
              </div>
              <div className='bg-white/5 rounded-lg p-4'>
                <div className='text-2xl font-bold text-white'>{campaign.stats?.totalClicked ?? 0}</div>
                <div className='text-sm text-gray-400'>Clicked</div>
              </div>
              <div className='bg-white/5 rounded-lg p-4'>
                <div className='text-2xl font-bold text-red-400'>{campaign.stats?.totalFailed ?? 0}</div>
                <div className='text-sm text-gray-400'>Failed</div>
              </div>
              <div className='bg-white/5 rounded-lg p-4'>
                <div className='text-2xl font-bold text-yellow-400'>{campaign.stats?.totalSkipped ?? 0}</div>
                <div className='text-sm text-gray-400'>Skipped</div>
              </div>
            </div>
          </div>

          {/* Schedule Info */}
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6'>
            <h3 className='text-lg font-semibold text-white mb-4'>Schedule</h3>
            <div className='space-y-3'>
              <div>
                <div className='text-sm text-gray-400'>Next Run</div>
                <div className='text-white'>{formatDate(campaign.nextRunAt)}</div>
              </div>
              <div>
                <div className='text-sm text-gray-400'>Last Run</div>
                <div className='text-white'>{formatDate(campaign.lastRunAt)}</div>
              </div>
              {campaign.schedule && (
                <>
                  <div>
                    <div className='text-sm text-gray-400'>Schedule Type</div>
                    <div className='text-white'>{campaign.schedule.type}</div>
                  </div>
                  <div>
                    <div className='text-sm text-gray-400'>Time</div>
                    <div className='text-white'>{campaign.schedule.time}</div>
                  </div>
                  {campaign.schedule.frequency && (
                    <div>
                      <div className='text-sm text-gray-400'>Frequency</div>
                      <div className='text-white'>{campaign.schedule.frequency}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Notification Content */}
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6'>
            <h3 className='text-lg font-semibold text-white mb-4'>Notification Content</h3>
            {campaign.inlineTemplate ? (
              <div className='space-y-3'>
                <div>
                  <div className='text-sm text-gray-400'>Title</div>
                  <div className='text-white font-medium'>{campaign.inlineTemplate.title}</div>
                </div>
                <div>
                  <div className='text-sm text-gray-400'>Body</div>
                  <div className='text-white'>{campaign.inlineTemplate.body}</div>
                </div>
                {hasTranslations(campaign.translations) && (
                  <div>
                    <div className='text-sm text-gray-400'>Translations</div>
                    <div className='text-white'>{Object.keys(campaign.translations || {}).join(', ')}</div>
                  </div>
                )}
                {campaign.inlineTemplate.imageUrl && (
                  <div>
                    <div className='text-sm text-gray-400 mb-2'>Image</div>
                    <img
                      src={campaign.inlineTemplate.imageUrl}
                      alt='Notification'
                      className='w-full h-32 object-cover rounded-lg'
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className='text-gray-400'>Using template: {campaign.templateId || 'None'}</div>
            )}
          </div>
        </div>

        {/* Audience Segment */}
        {campaign.segmentRule && (
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 mb-8'>
            <h3 className='text-lg font-semibold text-white mb-4'>Audience Segment</h3>
            <div className='space-y-2'>
              <div className='text-sm text-gray-400'>
                Operator: <span className='text-white'>{campaign.segmentRule.operator}</span>
              </div>
              <div className='flex flex-wrap gap-2'>
                {campaign.segmentRule.filters.map((filter, idx) => (
                  <span
                    key={idx}
                    className='inline-flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full text-sm text-gray-300'
                  >
                    {filter.field} {filter.operator} {JSON.stringify(filter.value)}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Rate Limiting */}
        {campaign.rateLimit && (
          <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 mb-8'>
            <h3 className='text-lg font-semibold text-white mb-4'>Rate Limiting</h3>
            <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
              {campaign.rateLimit.maxPerDay && (
                <div>
                  <div className='text-sm text-gray-400'>Max Per Day</div>
                  <div className='text-white'>{campaign.rateLimit.maxPerDay}</div>
                </div>
              )}
              {campaign.rateLimit.maxPerCampaign && (
                <div>
                  <div className='text-sm text-gray-400'>Max Per Campaign</div>
                  <div className='text-white'>{campaign.rateLimit.maxPerCampaign}</div>
                </div>
              )}
              {campaign.rateLimit.minHoursBetween && (
                <div>
                  <div className='text-sm text-gray-400'>Min Hours Between</div>
                  <div className='text-white'>{campaign.rateLimit.minHoursBetween}h</div>
                </div>
              )}
              {campaign.rateLimit.quietHoursStart && campaign.rateLimit.quietHoursEnd && (
                <div>
                  <div className='text-sm text-gray-400'>Quiet Hours</div>
                  <div className='text-white'>
                    {campaign.rateLimit.quietHoursStart} - {campaign.rateLimit.quietHoursEnd}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Run History */}
        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden'>
          <div className='px-6 py-4 border-b border-white/10'>
            <h3 className='text-lg font-semibold text-white'>Run History</h3>
          </div>

          {runsLoading && (
            <div className='flex justify-center items-center py-12'>
              <div className='animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#FF9C15]'></div>
            </div>
          )}

          {!runsLoading && runs.length === 0 && (
            <div className='text-center py-12'>
              <svg className='w-12 h-12 mx-auto text-gray-500 mb-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
              </svg>
              <p className='text-gray-400'>No runs yet</p>
            </div>
          )}

          {!runsLoading && runs.length > 0 && (
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead>
                  <tr className='bg-white/5'>
                    <th className='px-6 py-3 text-left text-sm font-semibold text-gray-300'>Run ID</th>
                    <th className='px-6 py-3 text-left text-sm font-semibold text-gray-300'>Status</th>
                    <th className='px-6 py-3 text-left text-sm font-semibold text-gray-300'>Started</th>
                    <th className='px-6 py-3 text-left text-sm font-semibold text-gray-300'>Completed</th>
                    <th className='px-6 py-3 text-left text-sm font-semibold text-gray-300'>Targeted</th>
                    <th className='px-6 py-3 text-left text-sm font-semibold text-gray-300'>Sent</th>
                    <th className='px-6 py-3 text-left text-sm font-semibold text-gray-300'>Failed</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr key={run._id} className='border-t border-white/5 hover:bg-white/5 transition-colors'>
                      <td className='px-6 py-4 text-sm text-gray-300 font-mono'>{run._id.slice(-8)}</td>
                      <td className='px-6 py-4'>
                        <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full text-white ${getRunStatusColor(run.status)}`}>
                          {run.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className='px-6 py-4 text-sm text-gray-300'>{formatDate(run.startedAt)}</td>
                      <td className='px-6 py-4 text-sm text-gray-300'>{formatDate(run.completedAt)}</td>
                      <td className='px-6 py-4 text-sm text-gray-300'>{run.stats.targeted}</td>
                      <td className='px-6 py-4 text-sm text-gray-300'>{run.stats.sent}</td>
                      <td className='px-6 py-4 text-sm text-red-400'>{run.stats.failed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div className='mt-8 text-sm text-gray-500'>
          <p>Created: {formatDate(campaign.createdAt)} by {campaign.createdBy || 'Unknown'}</p>
          <p>Last Updated: {formatDate(campaign.updatedAt)}</p>
        </div>
      </div>
    </div>
  );
};

export default CampaignDetail;
